import { BufferGeometry, Color, type ColorRepresentation, Float32BufferAttribute, Matrix3, Matrix4, Quaternion, Vector3 } from 'three';

/**
 * Accumulates low-poly pieces (each with one flat colour and a transform) and
 * merges them into a single non-indexed geometry with vertex colours. Flat
 * normals give the faceted, cut-paper look; `smooth` keeps a primitive's own
 * normals (helmets, hay bales).
 */
export class GeoBuilder {
  private pos: number[] = [];
  private nor: number[] = [];
  private col: number[] = [];
  private uv: number[] | null = null;

  constructor(withUv = false) {
    if (withUv) this.uv = [];
  }

  get vertexCount(): number {
    return this.pos.length / 3;
  }

  add(geo: BufferGeometry, color: ColorRepresentation | Color, m?: Matrix4, smooth = false): this {
    const g = geo.index ? geo.toNonIndexed() : geo.clone();
    if (m) g.applyMatrix4(m);
    if (!smooth) g.computeVertexNormals();
    const p = g.getAttribute('position');
    const n = g.getAttribute('normal');
    const uv = g.getAttribute('uv');
    const c = color instanceof Color ? color : new Color(color);
    for (let i = 0; i < p.count; i++) {
      this.pos.push(p.getX(i), p.getY(i), p.getZ(i));
      this.nor.push(n.getX(i), n.getY(i), n.getZ(i));
      this.col.push(c.r, c.g, c.b);
      if (this.uv) this.uv.push(uv ? uv.getX(i) : 0, uv ? uv.getY(i) : 0);
    }
    g.dispose();
    return this;
  }

  /** Raw triangles (already in world space), one colour per triangle vertex list. */
  addTriangles(positions: number[], color: Color, uvs?: number[]): this {
    for (let i = 0; i < positions.length; i += 9) {
      const ax = positions[i], ay = positions[i + 1], az = positions[i + 2];
      const bx = positions[i + 3], by = positions[i + 4], bz = positions[i + 5];
      const cx = positions[i + 6], cy = positions[i + 7], cz = positions[i + 8];
      const ux = bx - ax, uy = by - ay, uz = bz - az;
      const vx = cx - ax, vy = cy - ay, vz = cz - az;
      let nx = uy * vz - uz * vy;
      let ny = uz * vx - ux * vz;
      let nz = ux * vy - uy * vx;
      const l = Math.hypot(nx, ny, nz) || 1;
      nx /= l;
      ny /= l;
      nz /= l;
      for (let k = 0; k < 3; k++) {
        this.pos.push(positions[i + k * 3], positions[i + k * 3 + 1], positions[i + k * 3 + 2]);
        this.nor.push(nx, ny, nz);
        this.col.push(color.r, color.g, color.b);
      }
    }
    if (this.uv) {
      if (uvs) this.uv.push(...uvs);
      else for (let i = 0; i < (positions.length / 3) * 2; i++) this.uv.push(0);
    }
    return this;
  }

  /** Stamp a prebuilt prototype with a transform (fast path for thousands of pieces). */
  addProto(p: Proto, m: Matrix4): this {
    const e = m.elements;
    _nm.getNormalMatrix(m);
    const ne = _nm.elements;
    const P = p.pos;
    const N = p.nor;
    const C = p.col;
    for (let i = 0; i < P.length; i += 3) {
      const x = P[i], y = P[i + 1], z = P[i + 2];
      this.pos.push(e[0] * x + e[4] * y + e[8] * z + e[12], e[1] * x + e[5] * y + e[9] * z + e[13], e[2] * x + e[6] * y + e[10] * z + e[14]);
      const a = N[i], b = N[i + 1], c = N[i + 2];
      let nx = ne[0] * a + ne[3] * b + ne[6] * c;
      let ny = ne[1] * a + ne[4] * b + ne[7] * c;
      let nz = ne[2] * a + ne[5] * b + ne[8] * c;
      const l = Math.hypot(nx, ny, nz) || 1;
      nx /= l;
      ny /= l;
      nz /= l;
      this.nor.push(nx, ny, nz);
      this.col.push(C[i], C[i + 1], C[i + 2]);
    }
    if (this.uv) {
      const U = p.uv;
      for (let i = 0; i < P.length / 3; i++) this.uv.push(U ? U[i * 2] : 0, U ? U[i * 2 + 1] : 0);
    }
    return this;
  }

  toProto(): Proto {
    return {
      pos: Float32Array.from(this.pos),
      nor: Float32Array.from(this.nor),
      col: Float32Array.from(this.col),
      uv: this.uv ? Float32Array.from(this.uv) : null,
    };
  }

  build(): BufferGeometry {
    const g = new BufferGeometry();
    g.setAttribute('position', new Float32BufferAttribute(this.pos, 3));
    g.setAttribute('normal', new Float32BufferAttribute(this.nor, 3));
    g.setAttribute('color', new Float32BufferAttribute(this.col, 3));
    if (this.uv) g.setAttribute('uv', new Float32BufferAttribute(this.uv, 2));
    g.computeBoundingSphere();
    g.computeBoundingBox();
    return g;
  }
}

/** A prebuilt, flat-shaded, vertex-coloured piece in its own local frame. */
export interface Proto {
  pos: Float32Array;
  nor: Float32Array;
  col: Float32Array;
  uv: Float32Array | null;
}

const _nm = new Matrix3();
const _q = new Quaternion();
const _s = new Vector3();
const _p = new Vector3();
const _axis = new Vector3(0, 1, 0);

/** Transform helper: translate, rotate about Y (yaw), then uniform or per-axis scale. */
export function trs(x: number, y: number, z: number, yaw = 0, sx = 1, sy = sx, sz = sx): Matrix4 {
  _q.setFromAxisAngle(_axis, yaw);
  _p.set(x, y, z);
  _s.set(sx, sy, sz);
  return new Matrix4().compose(_p, _q, _s);
}

/** Deterministic pseudo-random for visual jitter (render side only). */
export function hash01(a: number, b = 0): number {
  let h = Math.imul(a | 0, 0x27d4eb2d) ^ Math.imul(b | 0, 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
